import PropTypes from 'prop-types';
// @flow

import React, { Component } from 'react';
import I18n from 'PMT/i18n/I18n';

import { Utils, ViewUtils } from 'PMT/utils';

// -- entities
import { ProductPrices } from 'PMT/entities/menu';

// -- views
import SvgIcon from 'PMT/components/SvgIcon';
import Switch from 'react-bootstrap-switch';
import { VatChooser, NumberInput, PriceInput } from 'PMT/components/form';


export default class PricesEditor extends Component {

  static defaultProps = {
    disablePriceModification: false,

    readOnly: false,
  };

  static propTypes = {
    prices: PropTypes.instanceOf(ProductPrices).isRequired,

    onChange: PropTypes.func.isRequired,

    /**
     * Disable price and tax modification, but allow to modify availability.
     */
    disablePriceModification: PropTypes.bool,

    readOnly: PropTypes.bool,

    currencyCode: PropTypes.string,
  };

  get prices(): ProductPrices {
    return this.props.prices;
  }

  //
  // Events toggle prices
  //

  onToggleTakeAway = () => {
    const prices:ProductPrices = this.prices;

    prices.takeAway.available = !prices.takeAway.available;

    this.props.onChange(prices);
  };

  onToggleDelivery = () => {
    const prices:ProductPrices = this.prices;

    prices.delivery.available = !prices.delivery.available;

    this.props.onChange(prices);
  };

  onToggleOnSite = () => {
    const prices:ProductPrices = this.prices;

    prices.onSite.available = !prices.onSite.available;

    this.props.onChange(prices);
  };

  //
  // Events change tax
  //

  onOnSiteVatChange = (tax: number) => {
    const prices:ProductPrices = this.prices;
    prices.onSite.vat = tax;
    this.props.onChange(prices);
  };

  onTakeAwayVatChange = (tax: number) => {
    const prices:ProductPrices = this.prices;
    prices.takeAway.vat = tax;
    this.props.onChange(prices);
  };

  onDeliveryVatChange = (tax: number) => {
    const prices:ProductPrices = this.prices;
    prices.delivery.vat = tax;
    this.props.onChange(prices);
  };

  //
  // Events change price
  //

  onOnSitePriceChange = (price: number) => {
    const prices:ProductPrices = this.prices;
    prices.onSite.setPriceWithTax(price);
    this.props.onChange(prices);
  };

  onTakeAwayPriceChange = (price: number) => {
    const prices:ProductPrices = this.prices;
    prices.takeAway.setPriceWithTax(price);
    this.props.onChange(prices);
  };

  onDeliveryPriceChange = (price: number) => {
    const prices:ProductPrices = this.prices;
    prices.delivery.setPriceWithTax(price);
    this.props.onChange(prices);
  };

  //
  // renderers
  //

  render() {
    return (
      <div className="prices_editor">
        <table className="table product_modal_view__prices_table">
          <thead className="thead-inverse">
            <tr>
              <th className="col-xs-1" />
              <th className="col-xs-2">
                {I18n.tr('components.prices_editor.mode')}
              </th>
              <th className="text-center col-xs-2">
                {I18n.tr('components.prices_editor.price_with_tax')}
              </th>
              <th className="text-center col-xs-3">
                {I18n.tr('components.prices_editor.vat')}
              </th>
              <th className="col-xs-1 text-center">
                {I18n.tr('components.prices_editor.activation')}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className=""><SvgIcon icon={SvgIcon.ORDER_ON_SITE} /></td>
              <td className="">
                {I18n.tr('components.prices_editor.on_site')}
              </td>
              <td className="text-center">
                <PriceInput
                  value={this.prices.onSite.priceIncludingTax}
                  currencyCode={this.props.currencyCode}
                  onChange={this.onOnSitePriceChange}
                  readOnly={this.props.disablePriceModification || this.props.readOnly}
                />
              </td>
              <td>
                <VatChooser
                  onChange={this.onOnSiteVatChange}
                  value={this.prices.onSite.vat}
                  clearable={false}
                  readOnly={this.props.disablePriceModification || this.props.readOnly}
                />
              </td>
              <td className="text-center">
                <Switch
                  key={Utils.generateUID()}
                  bsSize="mini"
                  value={this.prices.onSite.available}
                  onChange={this.onToggleOnSite}
                  disabled={this.props.readOnly}
                />
              </td>
            </tr>
            <tr>
              <td><SvgIcon icon={SvgIcon.ORDER_TAKE_AWAY} /></td>
              <td>
                {I18n.tr('components.prices_editor.take_away')}
              </td>
              <td className="text-center">
                <PriceInput
                  value={this.prices.takeAway.priceIncludingTax}
                  currencyCode={this.props.currencyCode}
                  onChange={this.onTakeAwayPriceChange}
                  readOnly={this.props.disablePriceModification || this.props.readOnly}
                />
              </td>
              <td>
                <VatChooser
                  onChange={this.onTakeAwayVatChange}
                  value={this.prices.takeAway.vat}
                  clearable={false}
                  readOnly={this.props.disablePriceModification || this.props.readOnly}
                />
              </td>
              <td className="text-center">
                <Switch
                  key={Utils.generateUID()}
                  bsSize="mini"
                  value={this.prices.takeAway.available}
                  onChange={this.onToggleTakeAway}
                  disabled={this.props.readOnly}
                />
              </td>
            </tr>
            <tr>
              <td><SvgIcon icon={SvgIcon.ORDER_DELIVERY} /></td>
              <td>
                {I18n.tr('components.prices_editor.delivery')}
              </td>
              <td className="text-center">
                <PriceInput
                  value={this.prices.delivery.priceIncludingTax}
                  currencyCode={this.props.currencyCode}
                  onChange={this.onDeliveryPriceChange}
                  readOnly={this.props.disablePriceModification || this.props.readOnly}
                />
              </td>
              <td>
                <VatChooser
                  onChange={this.onDeliveryVatChange}
                  value={this.prices.delivery.vat}
                  clearable={false}
                  readOnly={this.props.disablePriceModification || this.props.readOnly}
                />
              </td>
              <td className="text-center">
                <Switch
                  key={Utils.generateUID()}
                  bsSize="mini"
                  value={this.prices.delivery.available}
                  onChange={this.onToggleDelivery}
                  disabled={this.props.readOnly}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

}
